import { cn } from "@/shared/lib/cn";
import { labelClass } from "@/shared/ui/core/styles";
import { Minus, Plus } from "lucide-react";
import type { ReactNode } from "react";

type Props = {
  label?: string;
  required?: boolean;
  value: string;
  onChange: (v: string) => void;
  step?: number;
  disabled?: boolean;
  error?: string;
  hint?: string;
  uom?: string | null;
  /** Optional field adornment (CCP / critical only — §3.6). */
  startAdornment?: ReactNode;
};

function roundStep(n: number, step: number): number {
  const decimals = String(step).includes(".")
    ? (String(step).split(".")[1]?.length ?? 0)
    : 0;
  const f = 10 ** decimals;
  return Math.round(n * f) / f;
}

const stepBtnClass = cn(
  "inline-flex size-11 shrink-0 items-center justify-center",
  "bg-bg text-text-secondary transition-colors",
  "hover:bg-primary-soft hover:text-primary",
  "active:bg-primary/15",
  "disabled:cursor-not-allowed disabled:bg-bg disabled:text-text-disabled disabled:opacity-60",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-focus-ring)]",
);

/** Field capture — numpad decimal + ± step (ít gõ hơn Input thuần). */
export function NumberStepper({
  label,
  required = false,
  value,
  onChange,
  step = 1,
  disabled,
  error,
  hint,
  uom,
  startAdornment,
}: Props) {
  const bump = (dir: 1 | -1) => {
    const cur = Number(value);
    const base = Number.isFinite(cur) ? cur : 0;
    onChange(String(roundStep(base + dir * step, step)));
  };

  return (
    <div className="flex w-full flex-col gap-1.5">
      {label ? (
        <span className={cn(labelClass, "flex items-center gap-1")}>
          <span>
            {label}
            {uom ? ` (${uom})` : ""}
          </span>
          {required ? (
            <span
              className="text-sm font-bold leading-none text-danger"
              aria-hidden="true"
            >
              *
            </span>
          ) : null}
        </span>
      ) : null}
      <div
        className={cn(
          "flex h-12 w-full items-stretch overflow-hidden rounded-control border bg-surface",
          error ? "border-danger" : "border-border-strong",
          disabled && "opacity-70",
        )}
      >
        <button
          type="button"
          disabled={disabled}
          aria-label="Giảm"
          className={cn(stepBtnClass, "border-r border-border")}
          onClick={() => bump(-1)}
        >
          <Minus className="size-4" strokeWidth={2.5} aria-hidden />
        </button>
        <div className="relative flex min-w-0 flex-1 items-center">
          {startAdornment ? (
            <span className="pointer-events-none absolute left-2.5 text-text-muted">
              {startAdornment}
            </span>
          ) : null}
          <input
            inputMode="decimal"
            value={value}
            disabled={disabled}
            aria-invalid={error ? true : undefined}
            className={cn(
              "h-full w-full border-0 bg-transparent text-center font-mono text-lg font-bold tabular-nums text-text",
              "outline-none focus:ring-0",
              "disabled:cursor-not-allowed disabled:text-text-disabled",
              startAdornment ? "pl-9 pr-3" : "px-3",
            )}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
        <button
          type="button"
          disabled={disabled}
          aria-label="Tăng"
          className={cn(stepBtnClass, "border-l border-border")}
          onClick={() => bump(1)}
        >
          <Plus className="size-4" strokeWidth={2.5} aria-hidden />
        </button>
      </div>
      {error ? (
        <p className="m-0 text-caption text-danger" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p className="m-0 text-caption text-text-muted">{hint}</p>
      ) : null}
    </div>
  );
}

/** Step gợi ý theo đơn vị / mã param. */
export function suggestParamStep(
  uomLabel: string | null | undefined,
  paramCode: string,
): number {
  const u = (uomLabel ?? "").toLowerCase();
  const c = paramCode.toLowerCase();
  if (u.includes("brix") || c.includes("brix")) return 0.1;
  if (u.includes("°c") || u.includes("c") || c.includes("temp")) return 0.5;
  if (u.includes("%") || c.includes("pct") || c.includes("loss")) return 0.1;
  return 1;
}
